import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import { getMulchMapVersions, restoreMulchMapVersion } from "api/MulchMap";
import { MulchMap } from "api/MulchMap/types";
import moment from "moment-timezone";
import styles from "../styles.module.scss";

const SQFT_PER_SQ_METER = 10.7639104167;
const SQFT_PER_CU_YD_PER_INCH = 324;

function totalYards(map: MulchMap): number {
  if (!map?.zones?.length) return 0;
  let cuYd = 0;
  for (const z of map.zones) {
    const sqFt = z.areaSqMeters * SQFT_PER_SQ_METER;
    cuYd += (sqFt * z.depthInches) / SQFT_PER_CU_YD_PER_INCH;
  }
  return Math.round(cuYd * 10) / 10;
}

interface Props {
  locationId: string;
  /**
   * Session-scoped Map identifier. When present, history is restricted to
   * this Map's version chain — the correct shape in the multi-variant
   * world. When omitted, falls back to all versions at the Location
   * (legacy behavior, only useful for old callers).
   */
  mapGroupId?: string | null;
  currentMapId: string | null;
  onClose: () => void;
  onRestored: () => void;
}

const VersionHistoryModal: React.FC<Props> = ({
  locationId,
  mapGroupId,
  currentMapId,
  onClose,
  onRestored,
}) => {
  const [versions, setVersions] = useState<MulchMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMulchMapVersions(locationId, mapGroupId || undefined)
      .then((res) => {
        if (cancelled) return;
        setVersions(Array.isArray(res) ? res : []);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load version history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId, mapGroupId]);

  const handleRestore = async (versionId: string) => {
    setRestoring(versionId);
    setError(null);
    try {
      const res: any = await restoreMulchMapVersion(versionId);
      if (res?.id) {
        onRestored();
        onClose();
      } else {
        setError(res?.message || "Restore failed.");
      }
    } catch (e: any) {
      setError(e?.message || "Restore failed.");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>Version history</ModalHeader>
      <ModalBody>
        {loading && (
          <p style={{ color: "#717D96", fontSize: 13, margin: "8px 0" }}>
            Loading…
          </p>
        )}

        {!loading && versions.length === 0 && !error && (
          <p style={{ color: "#717D96", fontSize: 13, margin: "8px 0" }}>
            No saved versions for this site yet.
          </p>
        )}

        {error && <p className={styles.rfqWarning}>{error}</p>}

        {!loading && versions.length > 0 && (
          <div className={styles.versionList}>
            {versions.map((v, i) => {
              const isCurrent = v.id === currentMapId;
              const yards = totalYards(v);
              return (
                <div
                  key={v.id}
                  className={`${styles.versionRow} ${
                    isCurrent ? styles.versionRowCurrent : ""
                  }`}
                >
                  <div className={styles.versionRowMain}>
                    <div className={styles.versionRowHeader}>
                      <strong>Version {v.version ?? versions.length - i}</strong>
                      {isCurrent && (
                        <span className={styles.versionBadge}>Current</span>
                      )}
                    </div>
                    <div className={styles.versionRowMeta}>
                      {moment(v.updatedAt).format("MMM D, YYYY h:mm a")}
                      {v.updatedBy?.firstName || v.updatedBy?.lastName
                        ? ` · ${[v.updatedBy.firstName, v.updatedBy.lastName].filter(Boolean).join(" ")}`
                        : ""}
                    </div>
                    <div className={styles.versionRowStats}>
                      {v.zones?.length || 0} zone
                      {(v.zones?.length || 0) === 1 ? "" : "s"} · {yards.toFixed(1)}{" "}
                      cu yd
                      {v.basemap ? ` · ${v.basemap.replace(/_/g, " ")}` : ""}
                    </div>
                  </div>
                  {!isCurrent && (
                    <BasicButton
                      type="button"
                      scheme="secondary"
                      outline
                      onClick={() => handleRestore(v.id)}
                      disabled={!!restoring}
                      styles={{ margin: "0" }}
                      keepText
                    >
                      {restoring === v.id ? "Restoring…" : "Restore"}
                    </BasicButton>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <BasicButton
          type="button"
          scheme="secondary"
          outline
          onClick={onClose}
          styles={{ margin: "0" }}
        >
          Close
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default VersionHistoryModal;
